import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  ListItemText,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { Field } from 'formik';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';
import DayOfWeekField from '@sophocles/components/DayOfWeekField';
import { scheduleStart, scheduleDays, scheduleEnd } from '@sophocles/entities/RelaySchedule';
import { getTimeFormat } from '@sophocles/utils/formatUtils';
import { SmallTimePicker, deriveDirection } from '@sophocles/views/sites/components/relayScheduleFields';

export function dimmingData(schedule, t) {
  const value = schedule.dutyCycle ?? 100;
  return `${t('sites.brightness')}: ${value}%`;
}

// Direction lives in local state so toggling never depends on the sign of a
// numeric zero, and the magnitude input disappears when "At" is selected to
// remove the "do I need to type a number?" question.
function SunOffsetField({ field, form, meta, label, ariaLabel }) {
  const { t } = useTranslation();
  const [direction, setDirection] = useState(() => deriveDirection(field.value));

  const handleDirectionChange = (event, newValue) => {
    if (newValue === null) return;
    setDirection(newValue);
    if (newValue === 'at') {
      form.setFieldValue(field.name, 0);
      return;
    }
    const magnitude = Math.abs(field.value) || 0;
    form.setFieldValue(field.name, newValue === 'before' ? -magnitude : magnitude);
  };

  const handleValueChange = event => {
    const raw = parseInt(event.target.value, 10);
    const magnitude = Number.isFinite(raw) ? Math.abs(raw) : 0;
    form.setFieldValue(field.name, direction === 'before' ? -magnitude : magnitude);
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <ToggleButtonGroup
        exclusive
        color="primary"
        size="small"
        value={direction}
        onChange={handleDirectionChange}
        aria-label={ariaLabel}
      >
        <ToggleButton value="before" aria-label={t('general.before', 'Before')} sx={{ width: '100%' }}>
          <span>{t('general.before', 'Before')}</span>
        </ToggleButton>
        <ToggleButton value="at" aria-label={t('general.at', 'At')} sx={{ width: '100%' }}>
          <span>{t('general.at', 'At')}</span>
        </ToggleButton>
        <ToggleButton value="after" aria-label={t('general.after', 'After')} sx={{ width: '100%' }}>
          <span>{t('general.after', 'After')}</span>
        </ToggleButton>
      </ToggleButtonGroup>
      {direction !== 'at' && (
        <TextField
          {...field}
          fullWidth
          size="small"
          type="number"
          label={label}
          value={Math.abs(field.value)}
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
          onChange={handleValueChange}
        />
      )}
    </Stack>
  );
}

function renderScheduleForm(
  title,
  schedule,
  namePath = 'schedule',
  dimmerAvailable = false,
  is2PDC = false,
  supportsDayOfWeek = true,
  supportsNegativeOffsets = false,
  showAlwaysOn = false,
  collapsed = false,
  hideIsActive = false,
  isEditMode = false,
  onToggleEdit = null,
  setFieldValue,
  user = null,
  nextSunset = null,
  nextSunrise = null,
) {
  const { t } = useTranslation();

  const getFieldName = field => `${namePath}.${field}`;

  let days = scheduleDays(schedule.daysOfWeek, t);
  if (days.length == 7) {
    days = t('general.everyDay');
  } else if (days.length == 0) {
    days = t('general.noDaysSelected');
  } else {
    days = days.join(' ');
  }

  let primaryText = null;
  let secondaryText = null;
  let secondaryTextLines = 1;
  const alwaysOnText = schedule.scheduleNumber + `. ${t('sites.alwaysOn')}`;

  if (!schedule.enabled) {
    secondaryText = schedule.scheduleNumber + `. ${t('sites.inactiveSchedule')}`;
  } else if (schedule.isAlwaysOn) {
    primaryText = alwaysOnText;
    secondaryText = (
      <span className="schedule-list-secondary-text" style={{ display: 'flex', flexDirection: 'column' }}>
        {dimmerAvailable ? <span>{dimmingData(schedule, t)}</span> : null}
      </span>
    );
  } else if (supportsDayOfWeek) {
    primaryText = (
      <span>
        <b>{schedule.scheduleNumber}.</b> {days}
      </span>
    );
    secondaryTextLines = 2;
    secondaryText = (
      <span className="schedule-list-secondary-text" style={{ display: 'flex', flexDirection: 'column' }}>
        <span>
          {t('sites.on').toUpperCase()}:&nbsp;{' '}
          {scheduleStart(
            schedule.isAlwaysOn,
            schedule.onTime,
            schedule.onType,
            schedule.onOffset,
            nextSunrise,
            nextSunset,
            user,
            t,
          )}
        </span>
        <span>
          {t('sites.off').toUpperCase()}:{' '}
          {scheduleEnd(
            schedule.isAlwaysOn,
            schedule.offTime,
            schedule.offType,
            schedule.offOffset,
            nextSunrise,
            nextSunset,
            user,
            t,
          )}
        </span>
        {dimmerAvailable ? <span>{dimmingData(schedule, t)}</span> : null}
      </span>
    );
  } else {
    secondaryText = (
      <div
        className="schedule-list-secondary-text"
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
      >
        <div>
          <div>
            {t('sites.on').toUpperCase()}:&nbsp;{' '}
            {scheduleStart(
              schedule.isAlwaysOn,
              schedule.onTime,
              schedule.onType,
              schedule.onOffset,
              nextSunrise,
              nextSunset,
              user,
              t,
            )}
          </div>
          <div>
            {t('sites.off').toUpperCase()}:{' '}
            {scheduleEnd(
              schedule.isAlwaysOn,
              schedule.offTime,
              schedule.offType,
              schedule.offOffset,
              nextSunrise,
              nextSunset,
              user,
              t,
            )}
          </div>
          {dimmerAvailable ? <div>{dimmingData(schedule, t)}</div> : null}
        </div>
      </div>
    );
  }

  const onDone = () => {
    if (schedule.enabled && schedule.daysOfWeek.length == 0) {
      setFieldValue(getFieldName('enabled'), false);
    }
    onToggleEdit();
  };

  return (
    <Fragment>
      <Stack direction="row" justifyContent="space-between" sx={{ bgcolor: 'background.paper', p: 2 }}>
        <Typography
          variant="h6"
          sx={{
            textTransform: 'uppercase',
            fontSize: '1rem',
            lineHeight: '2.2',
            color: schedule.enabled ? '' : grey[500],
          }}
        >
          {title}
        </Typography>
        {hideIsActive === false && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Field name={getFieldName('enabled')}>
              {({ field }) => (
                <Switch {...field} checked={field.value} onChange={event => field.onChange(event)} sx={{ mx: 1 }} />
              )}
            </Field>
          </Stack>
        )}
      </Stack>
      {!isEditMode ? (
        <Box sx={{ p: 2 }}>
          <Stack flexDirection="row" alignItems="center">
            <ListItemText primary={primaryText} secondary={secondaryText} />
            <Button disabled={!schedule.enabled} onClick={onToggleEdit}>
              {t('actions.edit')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Fragment>
          {schedule.enabled && <Divider />}
          {schedule.enabled && (
            <Stack
              spacing={2}
              sx={{ display: collapsed ? 'none' : 'flex', m: 0, p: 2, bgcolor: 'var(--surface-secondary)' }}
            >
              {showAlwaysOn && (
                <Box display="flex" justifyContent="start" alignItems="center">
                  <FormControlLabel
                    label={t('sites.alwaysOn')}
                    control={
                      <Field name={getFieldName('isAlwaysOn')}>
                        {({ field }) => (
                          <Switch
                            {...field}
                            checked={field.value}
                            onChange={event => field.onChange(event)}
                            sx={{ mx: 1 }}
                          />
                        )}
                      </Field>
                    }
                  />
                </Box>
              )}

              {schedule.isAlwaysOn && (
                <Fragment>
                  {dimmerAvailable && (
                    <Stack direction="column" spacing={0}>
                      <Typography variant="body2">{dimmingData(schedule, t)}</Typography>
                      <Field name={getFieldName('dutyCycle')} type="number">
                        {({ field: { value }, form: { setFieldValue } }) => (
                          <Slider
                            value={value}
                            min={0}
                            max={100}
                            step={5}
                            marks
                            onChange={(event, value) => setFieldValue(getFieldName('dutyCycle'), value)}
                          />
                        )}
                      </Field>
                      {is2PDC && (
                        <Field name={getFieldName('frequency')}>
                          {({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              type="number"
                              label={t('sites.dimmingFrequency')}
                            />
                          )}
                        </Field>
                      )}
                    </Stack>
                  )}
                </Fragment>
              )}

              {!schedule.isAlwaysOn && (
                <Fragment>
                  <Stack direction="column" spacing={2}>
                    {supportsDayOfWeek && (
                      <DayOfWeekField required fullWidth name={getFieldName('daysOfWeek')} size="small" />
                    )}
                    {dimmerAvailable && (
                      <Stack direction="column" spacing={0}>
                        <Typography variant="body2">{dimmingData(schedule, t)}</Typography>
                        <Field name={getFieldName('dutyCycle')} type="number">
                          {({ field: { value }, form: { setFieldValue } }) => (
                            <Slider
                              value={value}
                              min={0}
                              max={100}
                              step={5}
                              marks
                              onChange={(event, value) => setFieldValue(getFieldName('dutyCycle'), value)}
                            />
                          )}
                        </Field>
                        {is2PDC && (
                          <Field name={getFieldName('frequency')}>
                            {({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                size="small"
                                type="number"
                                label={t('sites.dimmingFrequency')}
                              />
                            )}
                          </Field>
                        )}
                      </Stack>
                    )}
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack spacing={1} direction="row" alignItems="center">
                          <Typography variant="h6" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('sites.on')}
                          </Typography>
                        </Stack>
                        <Field name={getFieldName('onType')}>
                          {({ field, form }) => (
                            <ToggleButtonGroup
                              exclusive
                              color="primary"
                              size="small"
                              value={field.value}
                              onChange={(event, newValue) => {
                                if (newValue !== null) {
                                  form.setFieldValue(field.name, newValue);
                                }
                              }}
                            >
                              <ToggleButton value="time" key="time" sx={{ width: '100%' }}>
                                <span>{t('general.time')}</span>
                              </ToggleButton>
                              <ToggleButton value="sunset" key="sunset" sx={{ width: '100%' }}>
                                <span>{t('sites.sunset')}</span>
                              </ToggleButton>
                              {supportsNegativeOffsets && (
                                <ToggleButton value="sunrise" key="sunrise" sx={{ width: '100%' }}>
                                  <span>{t('sites.sunrise')}</span>
                                </ToggleButton>
                              )}
                            </ToggleButtonGroup>
                          )}
                        </Field>
                      </Stack>

                      {schedule.onType !== 'time' ? (
                        <Field name={getFieldName('onOffset')}>
                          {({ field, form, meta }) =>
                            supportsNegativeOffsets ? (
                              <SunOffsetField
                                field={field}
                                form={form}
                                meta={meta}
                                label={
                                  schedule.onType === 'sunset' ? t('sites.sunsetOffset') : t('sites.sunriseOffset')
                                }
                                ariaLabel={t('sites.onOffsetDirection', 'On Offset Direction')}
                              />
                            ) : (
                              <TextField
                                {...field}
                                fullWidth
                                size="small"
                                type="number"
                                label={
                                  schedule.onType === 'sunset' ? t('sites.sunsetOffset') : t('sites.sunriseOffset')
                                }
                                value={Math.abs(field.value)}
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                                onChange={event => {
                                  const value = parseInt(event.target.value, 10);
                                  const adjustedValue =
                                    schedule.onType === 'sunset' ? -Math.abs(value) : Math.abs(value);
                                  form.setFieldValue(field.name, adjustedValue);
                                }}
                              />
                            )
                          }
                        </Field>
                      ) : (
                        <Field name={getFieldName('onTime')}>
                          {({ field, form }) => (
                            <SmallTimePicker
                              format={getTimeFormat(user)}
                              value={field.value}
                              onChange={time => {
                                form.setFieldValue(field.name, time || null);
                              }}
                              slotProps={{
                                textField: {
                                  label: t('sites.onTime'),
                                  fullWidth: true,
                                },
                              }}
                            />
                          )}
                        </Field>
                      )}
                    </Stack>
                    <Stack direction="column" spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack spacing={1} direction="row" alignItems="center">
                          <Typography variant="h6" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('sites.off')}
                          </Typography>
                        </Stack>
                        <Field name={getFieldName('offType')}>
                          {({ field, form }) => (
                            <ToggleButtonGroup
                              exclusive
                              color="primary"
                              size="small"
                              value={field.value}
                              onChange={(event, newValue) => {
                                if (newValue !== null) {
                                  form.setFieldValue(field.name, newValue);
                                }
                              }}
                            >
                              <ToggleButton value="time" key="time" sx={{ width: '100%' }}>
                                <span>{t('general.time')}</span>
                              </ToggleButton>
                              {supportsNegativeOffsets && (
                                <ToggleButton value="sunset" key="sunset" sx={{ width: '100%' }}>
                                  <span>{t('sites.sunset')}</span>
                                </ToggleButton>
                              )}
                              <ToggleButton value="sunrise" key="sunrise" sx={{ width: '100%' }}>
                                <span>{t('sites.sunrise')}</span>
                              </ToggleButton>
                            </ToggleButtonGroup>
                          )}
                        </Field>
                      </Stack>
                      {schedule.offType !== 'time' ? (
                        <Field name={getFieldName('offOffset')}>
                          {({ field, form, meta }) =>
                            supportsNegativeOffsets ? (
                              <SunOffsetField
                                field={field}
                                form={form}
                                meta={meta}
                                label={
                                  schedule.offType === 'sunset' ? t('sites.sunsetOffset') : t('sites.sunriseOffset')
                                }
                                ariaLabel={t('sites.offOffsetDirection', 'Off Offset Direction')}
                              />
                            ) : (
                              <TextField
                                {...field}
                                fullWidth
                                size="small"
                                type="number"
                                label={
                                  schedule.offType === 'sunset' ? t('sites.sunsetOffset') : t('sites.sunriseOffset')
                                }
                                value={Math.abs(field.value)}
                                error={meta.touched && Boolean(meta.error)}
                                helperText={meta.touched && meta.error}
                                onChange={event => {
                                  const value = parseInt(event.target.value, 10);
                                  const adjustedValue =
                                    schedule.offType === 'sunset' ? -Math.abs(value) : Math.abs(value);
                                  form.setFieldValue(field.name, adjustedValue);
                                }}
                              />
                            )
                          }
                        </Field>
                      ) : (
                        <Field name={getFieldName('offTime')}>
                          {({ field, form }) => (
                            <SmallTimePicker
                              format={getTimeFormat(user)}
                              value={field.value}
                              onChange={time => {
                                form.setFieldValue(field.name, time || null);
                              }}
                              slotProps={{
                                textField: {
                                  label: t('sites.offTime'),
                                  fullWidth: true,
                                },
                              }}
                            />
                          )}
                        </Field>
                      )}
                    </Stack>
                  </Stack>
                </Fragment>
              )}
              <Button onClick={onDone}>{t('actions.done')}</Button>
            </Stack>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}

function RelaySchedulesForm({
  title,
  schedule,
  setFieldValue,
  dimmerAvailable = false,
  is2PDC = false,
  supportsDayOfWeek = true,
  supportsNegativeOffsets = false,
  collapsed = false,
  showAlwaysOn = false,
  namePath = '',
  hideIsActive = false,
  nextSunset = null,
  nextSunrise = null,
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const { user } = useUser();

  return renderScheduleForm(
    title,
    schedule,
    namePath,
    dimmerAvailable,
    is2PDC,
    supportsDayOfWeek,
    supportsNegativeOffsets,
    showAlwaysOn,
    collapsed,
    hideIsActive,
    isEditMode,
    () => setIsEditMode(!isEditMode),
    setFieldValue,
    user.toJS(),
    nextSunset,
    nextSunrise,
  );
}

export default RelaySchedulesForm;
